/**
 * ChatChart — inline chart renderer for agent replies carrying structured
 * chart payloads (V2d).
 *
 * Philosophy (per FORK-butler-buddy-v2d-chart-viz-2026-04-20.md):
 *   - Mixpanel-grade restraint. Monochrome gridlines, Butler `butler-600`
 *     primary fill, `marquee-500` magenta secondary accent, no gradient
 *     washes, no 3D, no neon.
 *   - Numbers are the loudest element. Tabular-nums + tight letter-spacing
 *     on axes + tooltip.
 *   - Two densities: `compact` fits inside a chat bubble (no legend, 220px
 *     max height); `canvas` fits the AgentCanvas right rail (legend, axis
 *     labels, 360px max height).
 *   - Read-only — no brush, drill-down, crosshair. v2 if operators want it.
 *   - Responsive width via Recharts `ResponsiveContainer`.
 *
 * Accessibility:
 *   - Root wrapper carries `role="img"` + derived `aria-label` so the
 *     chart presents as a single labeled graphic to screen readers. The
 *     Recharts primitives under it are decorative at the AT layer.
 */

import { useId, useMemo } from 'react'
import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart as RLineChart,
  Pie,
  PieChart as RPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
} from 'recharts'

import type {
  BarChart as BarChartPayload,
  BarChartDatum,
  ChatChart,
  DonutChart as DonutChartPayload,
  LineChart as LineChartPayload,
} from '@/types/chart'

export type ChatChartDensity = 'compact' | 'canvas'

export interface ChatChartProps {
  chart: ChatChart | null | undefined
  density?: ChatChartDensity
  className?: string
}

// ─── Palette ──────────────────────────────────────────────────────────────
// Butler palette hex values pulled from tailwind.config.js extend.colors.
// Tailwind utility classes can't be used inside recharts (SVG fill/stroke
// live on DOM props, not class-based). Hex is the pragmatic seam — the
// config keeps them authoritative and this file references them verbatim.
const BUTLER_500 = '#5A78FF'
const BUTLER_600 = '#5A78FF'
const BUTLER_400 = '#7B93FF'
const MARQUEE_500 = '#BB6BD9'
const MARQUEE_200 = '#D4A6E8'
const MIDWAY_500 = '#20B99D'
const INK_DEFAULT = '#44566C'
const INK_SOFT = '#8697A8'
const HAIRLINE = '#E9EBF2'
const ROSE_500 = '#e11d48' // tailwind rose-600 analogue for YoY down

const DONUT_ROTATION = [
  BUTLER_600,
  MARQUEE_500,
  MIDWAY_500,
  BUTLER_400,
  '#a8a4ff', // soft lilac for 5+ slices
  INK_SOFT,
  MARQUEE_200,
  BUTLER_500,
]

function resolveSemanticColor(color: BarChartDatum['color']): string {
  if (!color) return BUTLER_600
  if (color === 'up') return MARQUEE_500
  if (color === 'down') return ROSE_500
  if (color === 'flat') return INK_SOFT
  // Any string starting with `#` or a CSS color word is used verbatim.
  return color
}

function formatNumberShort(n: number): string {
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`
  // Preserve 1 decimal for small floats (pct deltas); integers for ints.
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toFixed(1)
}

function formatTooltipNumber(n: number): string {
  if (!Number.isFinite(n)) return '—'
  // Full precision in the tooltip — this is where operators look up the
  // real value. Delegate currency/unit formatting to the backend so the
  // component stays unit-agnostic.
  return n.toLocaleString('en-US')
}

function buildAriaLabel(chart: ChatChart): string {
  const parts = [`${chart.type} chart`, chart.title]
  if (chart.type === 'bar') {
    parts.push(`${chart.series.length} bar${chart.series.length === 1 ? '' : 's'}`)
  } else if (chart.type === 'line') {
    const pts = chart.series[0]?.data?.length ?? 0
    parts.push(`${pts} data point${pts === 1 ? '' : 's'}`)
  } else if (chart.type === 'donut') {
    parts.push(`${chart.slices.length} slice${chart.slices.length === 1 ? '' : 's'}`)
  }
  return parts.filter(Boolean).join(': ')
}

// ─── Chart-type renderers ─────────────────────────────────────────────────

interface DensityConfig {
  height: number
  showLegend: boolean
  axisFontSize: number
  titleMarginBottom: number
}

const DENSITY: Record<ChatChartDensity, DensityConfig> = {
  compact: { height: 220, showLegend: false, axisFontSize: 11, titleMarginBottom: 4 },
  canvas: { height: 360, showLegend: true, axisFontSize: 12, titleMarginBottom: 8 },
}

function BarChartRenderer({ chart, density }: { chart: BarChartPayload; density: DensityConfig }) {
  const horizontal = chart.orientation === 'horizontal'
  const data = chart.series.map((datum, idx) => ({
    label: datum.label,
    value: datum.value,
    fill: resolveSemanticColor(datum.color),
    _idx: idx,
  }))

  return (
    <ResponsiveContainer width="100%" height={density.height}>
      <RBarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, left: horizontal ? 16 : 4, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={HAIRLINE}
          horizontal={!horizontal}
          vertical={horizontal}
        />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tick={{ fill: INK_SOFT, fontSize: density.axisFontSize }}
              tickFormatter={formatNumberShort}
              axisLine={{ stroke: HAIRLINE }}
              tickLine={false}
              label={chart.xLabel ? { value: chart.xLabel, position: 'insideBottom', offset: -2, fill: INK_SOFT, fontSize: density.axisFontSize } : undefined}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fill: INK_DEFAULT, fontSize: density.axisFontSize }}
              axisLine={{ stroke: HAIRLINE }}
              tickLine={false}
              width={Math.min(160, 8 * Math.max(...data.map((d) => d.label.length), 4) + 16)}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="label"
              tick={{ fill: INK_DEFAULT, fontSize: density.axisFontSize }}
              axisLine={{ stroke: HAIRLINE }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fill: INK_SOFT, fontSize: density.axisFontSize }}
              tickFormatter={formatNumberShort}
              axisLine={{ stroke: HAIRLINE }}
              tickLine={false}
              label={chart.yLabel ? { value: chart.yLabel, angle: -90, position: 'insideLeft', fill: INK_SOFT, fontSize: density.axisFontSize } : undefined}
            />
          </>
        )}
        <Tooltip
          cursor={{ fill: `${BUTLER_600}10` }}
          contentStyle={{
            background: 'rgba(255,255,255,0.96)',
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 6,
            fontSize: density.axisFontSize,
            color: INK_DEFAULT,
            boxShadow: '0 2px 10px rgba(55, 70, 95, 0.08)',
          }}
          formatter={(value: unknown) => [formatTooltipNumber(Number(value)), '']}
          labelStyle={{ fontWeight: 600, color: INK_DEFAULT }}
        />
        <Bar dataKey="value" radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
          {data.map((entry) => (
            <Cell key={`cell-${entry._idx}`} fill={entry.fill} />
          ))}
          {density.showLegend ? null : (
            // In compact mode we omit the legend but keep a subtle value
            // label on each bar so the viewer can read the number without
            // a tooltip hover.
            <LabelList
              dataKey="value"
              position={horizontal ? 'right' : 'top'}
              formatter={formatNumberShort}
              style={{ fill: INK_SOFT, fontSize: density.axisFontSize - 1 }}
            />
          )}
        </Bar>
      </RBarChart>
    </ResponsiveContainer>
  )
}

function LineChartRenderer({ chart, density }: { chart: LineChartPayload; density: DensityConfig }) {
  // Recharts wants one row per x-tick with one key per series. Merge the
  // series on their x-values — missing points render as gaps.
  const xSet = new Set<string | number>()
  for (const s of chart.series) {
    for (const pt of s.data) xSet.add(pt.x)
  }
  const xs = Array.from(xSet)
  // Sort numerically when all xs are numbers; fall back to insertion order.
  const allNumeric = xs.every((x) => typeof x === 'number')
  const orderedXs = allNumeric ? xs.slice().sort((a, b) => Number(a) - Number(b)) : xs
  const rows = orderedXs.map((x) => {
    const row: Record<string, string | number> = { x }
    for (const s of chart.series) {
      const pt = s.data.find((p) => p.x === x)
      row[s.label] = pt ? pt.y : NaN
    }
    return row
  })

  const colorForSeries = (idx: number) => {
    if (idx === 0) return BUTLER_600
    if (idx === 1) return MARQUEE_500
    if (idx === 2) return MIDWAY_500
    return DONUT_ROTATION[(idx + 2) % DONUT_ROTATION.length]
  }

  return (
    <ResponsiveContainer width="100%" height={density.height}>
      <RLineChart data={rows} margin={{ top: 8, right: 16, left: 4, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={HAIRLINE} />
        <XAxis
          dataKey="x"
          tick={{ fill: INK_DEFAULT, fontSize: density.axisFontSize }}
          axisLine={{ stroke: HAIRLINE }}
          tickLine={false}
          label={chart.xLabel ? { value: chart.xLabel, position: 'insideBottom', offset: -2, fill: INK_SOFT, fontSize: density.axisFontSize } : undefined}
        />
        <YAxis
          tick={{ fill: INK_SOFT, fontSize: density.axisFontSize }}
          tickFormatter={formatNumberShort}
          axisLine={{ stroke: HAIRLINE }}
          tickLine={false}
          label={chart.yLabel ? { value: chart.yLabel, angle: -90, position: 'insideLeft', fill: INK_SOFT, fontSize: density.axisFontSize } : undefined}
        />
        <Tooltip
          contentStyle={{
            background: 'rgba(255,255,255,0.96)',
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 6,
            fontSize: density.axisFontSize,
            color: INK_DEFAULT,
            boxShadow: '0 2px 10px rgba(55, 70, 95, 0.08)',
          }}
          formatter={(value: unknown) => [formatTooltipNumber(Number(value)), '']}
          labelFormatter={(value: unknown) => (chart.xLabel ? `${chart.xLabel}: ${value}` : String(value))}
          labelStyle={{ fontWeight: 600, color: INK_DEFAULT }}
        />
        {density.showLegend ? <Legend wrapperStyle={{ fontSize: density.axisFontSize, color: INK_DEFAULT }} /> : null}
        {chart.series.map((s, idx) => (
          <Line
            key={s.label}
            type="monotone"
            dataKey={s.label}
            stroke={colorForSeries(idx)}
            strokeWidth={2}
            dot={{ r: 2.5, fill: colorForSeries(idx) }}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </RLineChart>
    </ResponsiveContainer>
  )
}

function DonutChartRenderer({ chart, density }: { chart: DonutChartPayload; density: DensityConfig }) {
  const slices = chart.slices.map((s, idx) => ({
    label: s.label,
    value: s.value,
    fill: s.color || DONUT_ROTATION[idx % DONUT_ROTATION.length],
  }))
  return (
    <div className="relative h-full w-full">
      <ResponsiveContainer width="100%" height={density.height}>
        <RPieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="85%"
            paddingAngle={1}
            stroke="#ffffff"
            strokeWidth={1}
            isAnimationActive={false}
          >
            {slices.map((s, idx) => (
              <Cell key={`slice-${idx}`} fill={s.fill} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: 'rgba(255,255,255,0.96)',
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 6,
              fontSize: density.axisFontSize,
              color: INK_DEFAULT,
              boxShadow: '0 2px 10px rgba(55, 70, 95, 0.08)',
            }}
            formatter={(value: unknown) => [formatTooltipNumber(Number(value)), '']}
            labelStyle={{ fontWeight: 600, color: INK_DEFAULT }}
          />
          {density.showLegend ? <Legend wrapperStyle={{ fontSize: density.axisFontSize, color: INK_DEFAULT }} /> : null}
        </RPieChart>
      </ResponsiveContainer>
      {chart.centerLabel ? (
        <div
          data-testid="chat-chart-donut-center"
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center font-display font-semibold tabular-nums text-ink-dark dark:text-slate-100"
          style={{ fontSize: density.axisFontSize + 2 }}
        >
          <span className="text-center">{chart.centerLabel}</span>
        </div>
      ) : null}
    </div>
  )
}

// ─── Public component ─────────────────────────────────────────────────────

export function ChatChart({ chart, density = 'compact', className }: ChatChartProps) {
  const titleId = useId()

  if (!chart || !chart.type) return null
  const densityConfig = DENSITY[density]
  const ariaLabel = buildAriaLabel(chart)

  // Empty-data graceful state — don't render a 0-row chart.
  const empty = useMemo(() => {
    if (chart.type === 'bar') return chart.series.length === 0
    if (chart.type === 'line') {
      return chart.series.every((s) => s.data.length === 0)
    }
    if (chart.type === 'donut') return chart.slices.length === 0
    return true
  }, [chart])

  if (empty) {
    return (
      <div
        data-testid="chat-chart"
        data-state="empty"
        className={['mt-3 rounded-md border border-hairline bg-surface-stripe p-3 text-xs text-ink-soft', className].filter(Boolean).join(' ')}
      >
        No data available to chart.
      </div>
    )
  }

  return (
    <figure
      role="img"
      aria-label={ariaLabel}
      aria-describedby={chart.title ? titleId : undefined}
      data-testid="chat-chart"
      data-chart-type={chart.type}
      data-density={density}
      className={['mt-3 rounded-md border border-hairline bg-white p-3 shadow-card-soft dark:border-slate-700 dark:bg-slate-800', className].filter(Boolean).join(' ')}
    >
      {chart.title ? (
        <figcaption
          id={titleId}
          className="mb-1 font-display text-sm font-semibold tracking-tight text-ink-dark dark:text-slate-100"
          style={{ marginBottom: densityConfig.titleMarginBottom }}
        >
          {chart.title}
        </figcaption>
      ) : null}
      <div style={{ height: densityConfig.height }}>
        {chart.type === 'bar' ? (
          <BarChartRenderer chart={chart} density={densityConfig} />
        ) : null}
        {chart.type === 'line' ? (
          <LineChartRenderer chart={chart} density={densityConfig} />
        ) : null}
        {chart.type === 'donut' ? (
          <DonutChartRenderer chart={chart} density={densityConfig} />
        ) : null}
      </div>
    </figure>
  )
}

export default ChatChart
